import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  Tabs, TabsList, TabsTrigger,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  type DataTableColumn, type RowActionItem,
} from '@/components/ui';
import { displayScheduleState } from '../../lib/displaySchedule';
import { countLivePopups, selectPopupRows, type PopupScope } from '../lib/homePopupListView';
import { useHomePopupsQuery, useHomePopupMutation } from '../hooks/useHomePopupsQuery';
import { HOME_POPUP_MALLS, HOME_POPUP_MALL_LABEL, type HomePopup, type HomePopupMall } from '../types/homePopup';

/**
 * 서버에서는 **전량을 한 번에** 받는다. 탭의 건수("노출 중 3 / 전체 12")가 정확하려면 전체를
 * 알아야 하고, 노출 중 필터도 전체 위에서 걸러야 페이지마다 개수가 들쭉날쭉하지 않다.
 * 화면 페이징은 이 전량 위에서 클라이언트가 자른다(배너 목록과 같은 방식).
 */
const FETCH_SIZE = 200;
const PAGE_SIZE = 20;

/**
 * 목록용 짧은 일시 표기(`YYYY-MM-DD HH:mm`).
 *
 * `toLocaleString('ko-KR')` 은 "2026. 9. 1. 오전 10:00:00" 처럼 길어서, 시작~종료 두 개를 한 칸에
 * 넣으면 노출 기간 열이 팝업명 열의 폭을 잡아먹는다. 목록에서 초 단위는 쓸모가 없으므로 버린다
 * (정확한 값은 상세 폼의 datetime-local 입력에서 확인한다).
 */
const formatDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * 노출 기간 표기. 시작은 inclusive, 종료는 exclusive 이며 둘 다 null 이면 상시 노출이다.
 * 한쪽만 있는 경우도 흔해서(예: 시작만 지정) 빈 쪽은 물결로 열어 둔다.
 */
const formatPeriod = (popup: HomePopup, always: string): string => {
  const from = formatDateTime(popup.displayStartAt);
  const to = formatDateTime(popup.displayEndAt);
  if (!from && !to) return always;
  return `${from || '—'} ~ ${to || '—'}`;
};

/** 노출 국가몰 요약. null/빈 배열은 BE 가 전체 노출로 해석한다. */
const formatMalls = (codes: string[] | null, all: string): string => {
  if (codes == null || codes.length === 0) return all;
  const known = HOME_POPUP_MALLS.filter((m) => codes.includes(m));
  if (known.length === 0) return all;
  if (known.length === HOME_POPUP_MALLS.length) return all;
  return known.map((m) => HOME_POPUP_MALL_LABEL[m as HomePopupMall]).join(', ');
};

export const HomePopupListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<HomePopup | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 기본은 '전체' — 목록의 주 작업이 등록/수정이라 비활성·예정 건도 보여야 한다.
  // '노출 중' 은 "지금 뭐가 떠 있나" 를 확인하는 용도의 opt-in 뷰.
  const [scope, setScope] = useState<PopupScope>('all');
  useEffect(() => setPage(1), [scope]);

  const query = useHomePopupsQuery({ page: 1, size: FETCH_SIZE });
  const { remove } = useHomePopupMutation();

  const allItems = useMemo(() => query.data?.items ?? [], [query.data]);
  const liveCount = useMemo(() => countLivePopups(allItems), [allItems]);
  const scopedItems = useMemo(() => selectPopupRows(allItems, scope), [allItems, scope]);

  const pageStart = (page - 1) * PAGE_SIZE;
  const items = useMemo(
    () => scopedItems.slice(pageStart, pageStart + PAGE_SIZE),
    [scopedItems, pageStart],
  );

  // 전량 로드 전제가 깨지면(팝업이 FETCH_SIZE 초과) 건수와 필터가 일부만 반영된다 — 조용히 틀리지 않게 알린다.
  const truncated = (query.data?.pagination?.totalItems ?? 0) > allItems.length;

  const columns: ReadonlyArray<DataTableColumn<HomePopup>> = [
    {
      // 유일하게 width 를 주지 않는 열 — 나머지 열의 고정폭을 뺀 나머지를 전부 가져간다.
      // 다른 열을 넓히면 그만큼 팝업명이 좁아지므로, 폭 조정은 항상 이 열을 기준으로 판단한다.
      key: 'title',
      header: t('homePopup.cols.title', '팝업명'),
      cell: (row) => (
        <Link
          to={`/cms/home-popups/${row.id}`}
          className="font-medium underline-offset-2 hover:underline"
          style={{ color: 'var(--content-strong)' }}
        >
          {row.title}
        </Link>
      ),
    },
    {
      key: 'popupKey',
      header: t('homePopup.cols.popupKey', '팝업 키'),
      width: 140,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.popupKey}
        </span>
      ),
    },
    {
      key: 'period',
      header: t('homePopup.cols.period', '노출 기간'),
      width: 230,
      cell: (row) => (
        <span
          className="whitespace-nowrap text-[12px] tabular-nums"
          style={{ color: 'var(--content-muted)' }}
        >
          {formatPeriod(row, t('homePopup.period.always', '상시 노출'))}
        </span>
      ),
    },
    {
      key: 'visibleMallCodes',
      // 최대 표기는 몰 3개("한국몰, 영문몰, 일본몰") — 4개 전부는 "전체" 한 단어로 줄어든다.
      header: t('homePopup.cols.malls', '노출 국가몰'),
      width: 150,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
          {formatMalls(row.visibleMallCodes, t('homePopup.malls.all', '전체'))}
        </span>
      ),
    },
    {
      key: 'device',
      header: t('homePopup.cols.device', '기기'),
      width: 80,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
          {t(`homePopup.device.${row.device}`, row.device)}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('homePopup.cols.sortOrder', '순서'),
      align: 'right',
      width: 64,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('homePopup.cols.isActive', '활성'),
      width: 150,
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusBadge
            label={row.isActive ? t('homePopup.active', '활성') : t('homePopup.inactive', '비활성')}
            variant={row.isActive ? 'success' : 'neutral'}
          />
          {/* 활성인데 예약 윈도우 밖이면 그 사유를 같이 보여준다 — 이게 없으면 "활성인데 왜 안 뜨지"를
              화면에서 알 수 없다(배너 목록과 같은 이유). */}
          {row.isActive && displayScheduleState(row) !== 'live' ? (
            <StatusBadge
              variant={displayScheduleState(row) === 'scheduled' ? 'info' : 'warning'}
              label={
                displayScheduleState(row) === 'scheduled'
                  ? t('homePopup.scheduleScheduled', '노출 예정')
                  : t('homePopup.scheduleEnded', '기간 종료')
              }
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => {
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('homePopup.edit', '수정'), onClick: () => navigate(`/cms/home-popups/${row.id}`) },
          {
            key: 'delete',
            label: t('homePopup.delete', '삭제'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('homePopup.rowActionsLabel', '팝업 액션')} actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title={t('homePopup.title', '홈 팝업 관리')}
      actions={
        <Link to="/cms/home-popups/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('homePopup.create', '팝업 등록')}
          </Button>
        </Link>
      }
    >
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {error}
        </div>
      )}

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('homePopup.loadFailed', '홈 팝업 목록을 불러오지 못했습니다.')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('homePopup.retry', '다시 시도')}
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Tabs value={scope} onValueChange={(v) => setScope(v as PopupScope)}>
              <TabsList>
                <TabsTrigger value="live">
                  {t('homePopup.scope.live', '노출 중')} ({liveCount})
                </TabsTrigger>
                <TabsTrigger value="all">
                  {t('homePopup.scope.all', '전체')} ({allItems.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {truncated ? (
            <p
              role="alert"
              className="mb-2 rounded-md p-2 text-xs"
              style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
            >
              {t('homePopup.truncated', {
                total: query.data?.pagination?.totalItems ?? 0,
                loaded: allItems.length,
                defaultValue:
                  '팝업이 {{total}}건인데 {{loaded}}건만 불러왔습니다. 탭의 건수와 필터가 일부만 반영되므로 담당자에게 알려주세요.',
              })}
            </p>
          ) : null}

          <DataTable
            columns={[...columns]}
            rows={[...items]}
            rowKey={(row) => row.id}
            emptyState={
              scope === 'live'
                ? t('homePopup.emptyLive', '지금 노출 중인 팝업이 없습니다. "전체" 탭에서 노출 기간과 활성 여부를 확인하세요.')
                : t('homePopup.empty', '등록된 홈 팝업이 없습니다.')
            }
            stickyHeader
          />

          {scopedItems.length > 0 ? (
            <Pagination
              total={scopedItems.length}
              currentPage={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              className="mt-4"
            />
          ) : null}
        </>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('homePopup.deleteTitle', '홈 팝업 삭제')}</DialogTitle>
            <DialogDescription>
              {t('homePopup.deleteDesc', {
                title: deleteTarget?.title ?? '',
                defaultValue: '"{{title}}" 팝업을 삭제할까요? 되돌릴 수 없습니다.',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('homePopup.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                setError(null);
                remove.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                  onError: (e) => {
                    setDeleteTarget(null);
                    setError(friendlyMessage(e, t('homePopup.deleteFailed', '삭제에 실패했습니다.')));
                  },
                });
              }}
            >
              {t('homePopup.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
